import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { heroImages } from '../data/mockData';
import { ChevronRight, Pencil, X } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { useCurrency } from '../context/CurrencyContext';
import { useAdminMode } from '../context/AdminModeContext';
import { getFeaturedProducts } from '../services/productService';
import { subscribeToNewsletter } from '../services/newsletterService';
import { getSiteContent } from '../services/siteContentService';

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const { formatMoney, getProductPrice } = useCurrency();
  const { adminModeEnabled } = useAdminMode();

  const hydratedRef = useRef(false);

  const [siteContent, setSiteContent] = useState(() => {
    try {
      return getSiteContent();
    } catch {
      return null;
    }
  });

  const [heroOverrides, setHeroOverrides] = useState({});
  const [featuredOverrides, setFeaturedOverrides] = useState({
    enabled: true,
    title: 'FEATURED PRODUCTS',
    subtitle: 'Discover our latest arrivals and bestsellers',
    order: [],
    productOverrides: {},
  });
  const [editor, setEditor] = useState(null);

  useEffect(() => {
    hydratedRef.current = true;
  }, []);

  useEffect(() => {
    const refresh = () => {
      try {
        setSiteContent(getSiteContent());
      } catch {
        setSiteContent(null);
      }
    };
    refresh();
    window.addEventListener('ditris:siteContent', refresh);
    return () => window.removeEventListener('ditris:siteContent', refresh);
  }, []);

  useEffect(() => {
    const loadFeaturedProducts = async () => {
      try {
        const products = await getFeaturedProducts();
        setFeaturedProducts(products);
      } catch (error) {
        console.error('Error loading featured products:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadFeaturedProducts();
  }, []);

  const featuredProductsWithOverrides = useMemo(() => {
    const overrides = featuredOverrides.productOverrides || {};
    return featuredProducts.map((p) => {
      const o = overrides[p.id];
      if (!o) return p;
      return {
        ...p,
        name: typeof o.name === 'string' && o.name ? o.name : p.name,
        images: typeof o.image === 'string' && o.image ? [o.image, ...(p.images || []).slice(1)] : p.images,
      };
    });
  }, [featuredProducts, featuredOverrides.productOverrides]);

  const mergedHeroImages = useMemo(() => {
    return heroImages.map((hero) => {
      const override = heroOverrides[hero.id];
      return override ? { ...hero, ...override } : hero;
    });
  }, [heroOverrides]);

  const orderedFeaturedProducts = useMemo(() => {
    if (!featuredOverrides.order || featuredOverrides.order.length === 0) return featuredProductsWithOverrides;
    const byId = new Map(featuredProductsWithOverrides.map((p) => [p.id, p]));
    const ordered = featuredOverrides.order.map((id) => byId.get(id)).filter(Boolean);
    const leftovers = featuredProductsWithOverrides.filter((p) => !featuredOverrides.order.includes(p.id));
    return [...ordered, ...leftovers];
  }, [featuredOverrides.order, featuredProductsWithOverrides]);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (email) {
      try {
        await subscribeToNewsletter(email);
        toast({
          title: "Subscribed!",
          description: "Thank you for subscribing to our newsletter.",
        });
        setEmail('');
      } catch (error) {
        toast({
          title: "Subscription saved locally",
          description: "Add Firebase credentials to sync with database.",
        });
        setEmail('');
      }
    }
  };

  return (
    <div>
      {/* Hero Section */}
      <div className="relative">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
          {mergedHeroImages.map((hero) => {
            if (hero.disabled && !adminModeEnabled) return null;

            const openHeroEditor = (e) => {
              if (e) {
                e.preventDefault();
                e.stopPropagation();
              }
              setEditor({
                type: 'hero',
                id: hero.id,
                title: hero.title,
                link: hero.link,
                image: hero.image,
                disabled: !!hero.disabled,
              });
            };

            if (hero.disabled && adminModeEnabled) {
              return (
                <div key={hero.id} className="relative group overflow-hidden">
                  <button
                    type="button"
                    onClick={openHeroEditor}
                    className="absolute top-4 right-4 z-20 opacity-100 transition-opacity bg-white/90 backdrop-blur border border-gray-200 rounded-full p-2 shadow-sm"
                    aria-label="Edit hero banner"
                  >
                    <Pencil size={16} className="text-black" />
                  </button>
                  <div className="aspect-[3/4] overflow-hidden">
                    <img src={hero.image} alt={hero.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="absolute inset-0 bg-black/55" />
                  <div className="absolute bottom-8 left-8">
                    <div className="text-white text-xs tracking-widest font-medium mb-3">DISABLED</div>
                    <h2 className="text-white text-3xl md:text-4xl font-medium mb-2 tracking-wide">{hero.title}</h2>
                    <button
                      type="button"
                      onClick={openHeroEditor}
                      className="text-white underline text-sm font-medium"
                    >
                      Edit to re-enable
                    </button>
                  </div>
                </div>
              );
            }

            return (
              <Link key={hero.id} to={hero.link} className="relative group overflow-hidden">
                {adminModeEnabled && (
                  <button
                    type="button"
                    onClick={openHeroEditor}
                    className="absolute top-4 right-4 z-20 opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 backdrop-blur border border-gray-200 rounded-full p-2 shadow-sm"
                    aria-label="Edit hero banner"
                  >
                    <Pencil size={16} className="text-black" />
                  </button>
                )}
                <div className="aspect-[3/4] overflow-hidden">
                  <img
                    src={hero.image}
                    alt={hero.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="absolute bottom-8 left-8">
                  <h2 className="text-white text-3xl md:text-4xl font-medium mb-2 tracking-wide">{hero.title}</h2>
                  <span className="text-white flex items-center gap-2 text-sm font-medium">
                    SHOP NOW
                    <ChevronRight size={16} />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Featured Products */}
      {featuredOverrides.enabled && (
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="mb-8">
            <div className="relative group">
              {adminModeEnabled && (
                <button
                  type="button"
                  onClick={() => {
                    setEditor({
                      type: 'featured',
                      title: featuredOverrides.title,
                      subtitle: featuredOverrides.subtitle,
                      enabled: featuredOverrides.enabled,
                    });
                  }}
                  className="absolute -top-2 right-0 z-10 opacity-0 group-hover:opacity-100 transition-opacity bg-white border border-gray-200 rounded-full p-2 shadow-sm"
                  aria-label="Edit featured section"
                >
                  <Pencil size={16} className="text-black" />
                </button>
              )}
              <h2 className="text-2xl font-medium tracking-wide mb-2">{featuredOverrides.title}</h2>
              <p className="text-gray-600">{featuredOverrides.subtitle}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {orderedFeaturedProducts.map((product, idx) => (
              <Link
                key={product.id}
                to={`/product/${product.id}`}
                className="group relative"
              >
                {adminModeEnabled && (
                  <div className="absolute top-2 right-2 z-10 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setEditor({
                          type: 'featuredProduct',
                          id: product.id,
                          name: product.name,
                          image: product.images?.[0] || '',
                        });
                      }}
                      className="bg-white/90 backdrop-blur border border-gray-200 rounded-full p-2 shadow-sm"
                      aria-label="Edit featured product"
                    >
                      <Pencil size={16} className="text-black" />
                    </button>
                    <div className="bg-white/90 backdrop-blur border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                      <button
                        type="button"
                        disabled={idx === 0}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setFeaturedOverrides((prev) => {
                            const base = prev.order && prev.order.length ? prev.order.slice() : orderedFeaturedProducts.map((p) => p.id);
                            const i = base.indexOf(product.id);
                            if (i > 0) {
                              const t = base[i - 1];
                              base[i - 1] = base[i];
                              base[i] = t;
                            }
                            return { ...prev, order: base };
                          });
                        }}
                        className={`block w-full px-3 py-2 text-xs font-medium ${idx === 0 ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-50'}`}
                      >
                        Move Up
                      </button>
                      <button
                        type="button"
                        disabled={idx === orderedFeaturedProducts.length - 1}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setFeaturedOverrides((prev) => {
                            const base = prev.order && prev.order.length ? prev.order.slice() : orderedFeaturedProducts.map((p) => p.id);
                            const i = base.indexOf(product.id);
                            if (i >= 0 && i < base.length - 1) {
                              const t = base[i + 1];
                              base[i + 1] = base[i];
                              base[i] = t;
                            }
                            return { ...prev, order: base };
                          });
                        }}
                        className={`block w-full px-3 py-2 text-xs font-medium ${idx === orderedFeaturedProducts.length - 1 ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-50'}`}
                      >
                        Move Down
                      </button>
                    </div>
                  </div>
                )}
                <div className="aspect-square bg-gray-100 mb-3 overflow-hidden">
                  <img
                    src={product.images[0]}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <h3 className="text-sm font-medium mb-1">{product.name}</h3>
                <p className="text-sm">{formatMoney(getProductPrice(product))}</p>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/shop/new-arrivals"
              className="inline-block border border-black px-8 py-3 text-sm font-medium tracking-wide hover:bg-black hover:text-white transition-colors"
            >
              VIEW ALL PRODUCTS
            </Link>
          </div>
        </div>
      )}
      {!featuredOverrides.enabled && adminModeEnabled && (
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="rounded-2xl border border-dashed border-gray-300 p-8 text-center relative group">
            <button
              type="button"
              onClick={() => {
                setEditor({
                  type: 'featured',
                  title: featuredOverrides.title,
                  subtitle: featuredOverrides.subtitle,
                  enabled: featuredOverrides.enabled,
                });
              }}
              className="absolute top-4 right-4 z-10 opacity-0 group-hover:opacity-100 transition-opacity bg-white border border-gray-200 rounded-full p-2 shadow-sm"
              aria-label="Edit featured section"
            >
              <Pencil size={16} className="text-black" />
            </button>
            <div className="text-sm font-medium tracking-wide text-gray-900">FEATURED SECTION DISABLED</div>
            <div className="mt-2 text-sm text-gray-600">Edit to re-enable this section.</div>
          </div>
        </div>
      )}
      {/* Newsletter Section */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-2xl font-medium tracking-wide mb-4">{siteContent?.newsletter?.title || 'STAY IN THE LOOP'}</h2>
          <p className="text-gray-600 mb-6">
            {siteContent?.newsletter?.subtitle || 'Be the first to know about new arrivals, exclusive offers, and more.'}
          </p>
          <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="flex-1 border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors"
            />
            <button
              type="submit"
              className="bg-black text-white px-8 py-3 text-sm font-medium tracking-wide hover:bg-gray-800 transition-colors"
            >
              SUBSCRIBE
            </button>
          </form>
        </div>
      </div>

      {adminModeEnabled && editor && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-6 py-10">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            onClick={() => setEditor(null)}
            aria-label="Close editor"
          />
          <div className="relative w-full max-w-lg bg-white rounded-2xl border border-gray-200 shadow-[0_20px_60px_rgba(0,0,0,0.18)] p-6">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium tracking-wide">EDIT CONTENT</div>
              <button
                type="button"
                onClick={() => setEditor(null)}
                className="rounded-full border border-gray-200 p-2 hover:bg-gray-50 transition-colors"
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>

            {editor.type === 'hero' && (
              <div className="mt-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-800">Title</label>
                  <input
                    value={editor.title}
                    onChange={(e) => setEditor((prev) => ({ ...prev, title: e.target.value }))}
                    className="mt-2 w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-800">CTA Link</label>
                  <input
                    value={editor.link}
                    onChange={(e) => setEditor((prev) => ({ ...prev, link: e.target.value }))}
                    className="mt-2 w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-800">Image URL</label>
                  <input
                    value={editor.image}
                    onChange={(e) => setEditor((prev) => ({ ...prev, image: e.target.value }))}
                    className="mt-2 w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors"
                  />
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={!!editor.disabled}
                    onChange={(e) => setEditor((prev) => ({ ...prev, disabled: e.target.checked }))}
                  />
                  Disable this banner
                </label>
                <div className="pt-2 flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setHeroOverrides((prev) => ({
                        ...prev,
                        [editor.id]: {
                          title: editor.title,
                          link: editor.link,
                          image: editor.image,
                          disabled: !!editor.disabled,
                        },
                      }));
                      setEditor(null);
                    }}
                    className="flex-1 bg-black text-white rounded-lg px-4 py-3 text-sm font-medium hover:bg-gray-900 transition-colors"
                  >
                    Apply
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditor(null)}
                    className="flex-1 border border-gray-300 rounded-lg px-4 py-3 text-sm font-medium hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {editor.type === 'featured' && (
              <div className="mt-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-800">Title</label>
                  <input
                    value={editor.title}
                    onChange={(e) => setEditor((prev) => ({ ...prev, title: e.target.value }))}
                    className="mt-2 w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-800">Subtitle</label>
                  <input
                    value={editor.subtitle}
                    onChange={(e) => setEditor((prev) => ({ ...prev, subtitle: e.target.value }))}
                    className="mt-2 w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors"
                  />
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={!!editor.enabled}
                    onChange={(e) => setEditor((prev) => ({ ...prev, enabled: e.target.checked }))}
                  />
                  Enabled
                </label>
                <div className="pt-2 flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setFeaturedOverrides((prev) => ({
                        ...prev,
                        enabled: !!editor.enabled,
                        title: editor.title,
                        subtitle: editor.subtitle,
                      }));
                      setEditor(null);
                    }}
                    className="flex-1 bg-black text-white rounded-lg px-4 py-3 text-sm font-medium hover:bg-gray-900 transition-colors"
                  >
                    Apply
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditor(null)}
                    className="flex-1 border border-gray-300 rounded-lg px-4 py-3 text-sm font-medium hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {editor.type === 'featuredProduct' && (
              <div className="mt-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-800">Name (preview)</label>
                  <input
                    value={editor.name}
                    onChange={(e) => setEditor((prev) => ({ ...prev, name: e.target.value }))}
                    className="mt-2 w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-800">Primary image URL (preview)</label>
                  <input
                    value={editor.image}
                    onChange={(e) => setEditor((prev) => ({ ...prev, image: e.target.value }))}
                    className="mt-2 w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors"
                  />
                </div>
                <div className="pt-2 flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setFeaturedOverrides((prev) => ({
                        ...prev,
                        productOverrides: {
                          ...(prev.productOverrides || {}),
                          [editor.id]: {
                            name: editor.name,
                            image: editor.image,
                          },
                        },
                      }));
                      setEditor(null);
                    }}
                    className="flex-1 bg-black text-white rounded-lg px-4 py-3 text-sm font-medium hover:bg-gray-900 transition-colors"
                  >
                    Apply
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditor(null)}
                    className="flex-1 border border-gray-300 rounded-lg px-4 py-3 text-sm font-medium hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;